import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useOrderDetailsFilterQuery, useOrderStatusesQuery } from "@queries/order";
import { useTimeSlotsQuery } from "@queries/time_slots";
import { getServiceWindow } from "@lib/orderSort";
import { paymentInfo } from "@lib/paymentMethods";
import "@styles/opencart-admin.css";

const TYPES = ["express", "furniture", "housekeeping", "carpets", "car_cleaning", "all"];

const fromSearch = (sp) => {
  const hasParams = Array.from(sp.keys()).length > 0;
  return {
    day: sp.get("day") || (hasParams ? "" : new Date().toISOString().slice(0, 10)),
    time_slot: sp.get("time_slot") || "",
    order_status_id: sp.get("order_status_id") || "",
    page: Math.max(1, parseInt(sp.get("page") || "1", 10)),
    page_size: Math.max(1, parseInt(sp.get("page_size") || "100", 10)),
  };
};

const toSearch = (f) => {
  const sp = new URLSearchParams();
  if (f.day) sp.set("day", f.day);
  if (f.time_slot) sp.set("time_slot", f.time_slot);
  if (f.order_status_id) sp.set("order_status_id", f.order_status_id);
  sp.set("page", String(f.page || 1));
  sp.set("page_size", String(f.page_size || 100));
  return sp;
};

function normalizeColor(v) {
  if (!v || typeof v !== "string") return undefined;
  const s = v.trim();
  if (s.startsWith("#")) return s;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(s)) return s;
  const test = new Option().style;
  test.color = s;
  return test.color ? s : undefined;
}

function removeLastColumn(tableEl) {
  const removeFromRow = (row) => {
    const cells = row ? Array.from(row.children) : [];
    if (cells.length) cells[cells.length - 1].remove();
  };

  const theadRow = tableEl.querySelector("thead tr");
  removeFromRow(theadRow);

  const bodyRows = tableEl.querySelectorAll("tbody tr");
  bodyRows.forEach((r) => removeFromRow(r));
}

function printTableOnly(tableEl, title) {
  if (!tableEl) return;

  const w = window.open("", "_blank", "width=1100,height=750");
  if (!w) return;

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${String(title || "Print")}</title>
  <style>
    html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
    .page { padding: 18px; }
    .hdr { margin-bottom: 12px; }
    .hdr h2 { margin: 0 0 6px 0; font-size: 16px; }
    .hdr .meta { font-size: 12px; color: #444; }

    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #cfcfcf; padding: 6px 8px; font-size: 12px; vertical-align: top; }
    th { font-weight: 700; }
    td { word-break: break-word; }

    /* Hide Action column (last column) */
    thead tr > th:last-child,
    tbody tr > td:last-child { display: none !important; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      a { color: inherit; text-decoration: none; }
      .page { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="hdr">
      <h2>${String(title || "Orders")}</h2>
      <div class="meta">Printed: ${new Date().toLocaleString()}</div>
    </div>
    ${tableEl.outerHTML}
  </div>
</body>
</html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();

  w.onload = () => {
    w.focus();
    w.print();
  };

  w.onafterprint = () => {
    w.close();
  };
}


export default function OrdersByDate() {
  const [search, setSearch] = useSearchParams();
  const [filters, setFilters] = React.useState(fromSearch(search));
  
  React.useEffect(() => {
    const newFilters = fromSearch(search);
    setFilters(newFilters);
    
    // If there are no search params, set the default ones in the URL
    if (Array.from(search.keys()).length === 0) {
      setSearch(toSearch(newFilters), { replace: true });
    }
  }, [search, setSearch]);

  const { data: timeSlots = [], isLoading: tsLoading } = useTimeSlotsQuery(filters.day);

  const queryFilters = React.useMemo(
    () => ({
      from_date: filters.day || undefined,
      to_date: filters.day || undefined,
      time_slot: filters.time_slot || undefined,
      order_status_id: filters.order_status_id || undefined,
      page: filters.page || 1,
      page_size: filters.page_size || 100,
    }),
    [filters]
  );

  // Follow the super admin's selected marketplace so switching WashyWash <-> FlashWash
  // scopes the day's orders to that marketplace (WN-0140).
  const { data: resp, isLoading, isFetching } = useOrderDetailsFilterQuery(queryFilters, { respectMarketplace: true });
  const rows = resp?.data ?? [];
  const meta = resp?.meta ?? { page: filters.page, page_size: filters.page_size, total: rows.length, pages: 1 };

  const { data: statuses = [] } = useOrderStatusesQuery();

  const grouped = React.useMemo(() => {
    const g = {};
    for (const r of rows) {
      const t = r.type || "unknown";
      if (!g[t]) g[t] = [];
      g[t].push(r);
    }
    return g;
  }, [rows]);

  const apply = () => setSearch(toSearch({ ...filters, page: 1 }), { replace: true });
  const setPage = (p) => setSearch(toSearch({ ...filters, page: p }), { replace: true });

  const tableRefs = React.useRef({});

  const onPrintType = (type) => {
    const tableEl = tableRefs.current[type];
    const title = `${labelForType(type)} Orders • Date: ${filters.day || "-" } • Count: ${
      (type === "all" ? rows : grouped[type] || []).length
    }`;
    printTableOnly(tableEl, title);
  };

  const onExportCsv = (type) => {
    // Export exactly what's on screen (WN-0158). The previous version filtered
    // to a status subset [Cleaning/Delivery/Complete] and dropped complementary
    // rows, which silently omitted FOC / total=0 orders (e.g. Processing/Pick-up
    // orders) that ARE shown in the table. The CSV must match the table exactly.
    const typeRows = type === "all" ? rows : grouped[type] || [];
    if (!typeRows.length) {
      alert("No orders to export.");
      return;
    }

    const headers = [
      "Order ID",
      "Customer",
      "Next Status",
      "Timeslot",
      "Type",
      "Payment",
      "Order Total",
      "1st",
      "Area",
      "Customer & Order Notes",
      "Edited By"
    ];

    const csvRows = typeRows.map(r => {
      const notesRaw = r.notes_display || "";
      // Strip HTML tags and replace with divider for CSV
      const notes = notesRaw.replace(/<br[^>]*>/g, " - ").replace(/<[^>]*>/g, "");
      
      return [
        r.order_id,
        r.customer || "",
        r.next_status_text || "",
        r.next_ts || getServiceWindow(r) || "",
        r.type || "",
        r.payment ? paymentInfo(r.payment).label : "",
        isNaN(Number(r.total || r.order_total)) ? (r.total || r.order_total || "") : Number(r.total || r.order_total || 0).toFixed(2),
        r.first_order ? "Yes" : "No",
        r.area || "",
        notes,
        r.edited_by || ""
      ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(",");
    });

    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${labelForType(type)}_Orders_${filters.day || "all"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <div className="oc-card">Loading…</div>}

      <div className="oc-header">
        <h1>Orders Details</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Orders</span>
        </nav>
      </div>

      <div className="oc-card oc-filter">
        <div className="title">Filter</div>
        <div style={{ display: "grid", gridTemplateColumns: "260px 220px 260px 120px auto", gap: 12, alignItems: "end" }}>
          <div>
            <label className="lbl">Delivery/Pickup Day</label>
            <input
              type="date"
              value={filters.day}
              onChange={(e) => setFilters((f) => ({ ...f, day: e.target.value }))}
              className="oc-input"
            />
          </div>

          <div>
            <label className="lbl">Time Slot</label>
            <select
              value={filters.time_slot}
              onChange={(e) => setFilters((f) => ({ ...f, time_slot: e.target.value }))}
              className="oc-input"
              disabled={tsLoading}
            >
              <option value="">All</option>
              {timeSlots.map((t) => {
                const value = t?.value ?? t;
                const label = t?.label ?? t;
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="lbl">Status</label>
            <select
              value={filters.order_status_id}
              onChange={(e) => setFilters((f) => ({ ...f, order_status_id: e.target.value }))}
              className="oc-input"
            >
              <option value="">All</option>
              {statuses.map((s) => (
                <option key={s.order_status_id} value={s.order_status_id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button className="oc-btn" onClick={apply}>
              Filter
            </button>
          </div>
        </div>
      </div>

      {TYPES.filter((t) => (t === "all" ? rows.length : grouped[t]?.length)).map((type) => {
        const typeRows = type === "all" ? rows : grouped[type] || [];
        return (
          <div key={type} className="oc-card" style={{ marginTop: 16 }}>
            <div className="oc-toolbar" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <strong>
                  {labelForType(type)} Orders • Date: {filters.day || "-"} • Orders count: {typeRows.length}
                </strong>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button className="oc-btn ghost" onClick={() => onExportCsv(type)}>
                  Export CSV
                </button>
                <button className="oc-btn ghost" onClick={() => onPrintType(type)}>
                  Print
                </button>
              </div>
            </div>

            <OrdersTypeTable
              rows={typeRows}
              tableRef={(el) => {
                if (el) tableRefs.current[type] = el;
              }}
            />
          </div>
        );
      })}

      {!isLoading && rows.length === 0 && <div className="oc-card">No results</div>}

      <div className="oc-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
        <div>
          Showing page <strong>{meta.page}</strong> of <strong>{meta.pages}</strong> ({meta.total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="oc-btn ghost" disabled={meta.page <= 1} onClick={() => setPage(1)}>
            « First
          </button>
          <button className="oc-btn ghost" disabled={meta.page <= 1} onClick={() => setPage(meta.page - 1)}>
            ‹ Prev
          </button>
          <button className="oc-btn ghost" disabled={meta.page >= meta.pages} onClick={() => setPage(meta.page + 1)}>
            Next ›
          </button>
          <button className="oc-btn ghost" disabled={meta.page >= meta.pages} onClick={() => setPage(meta.pages)}>
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

function OrdersTypeTable({ rows, tableRef }) {
  const [hoveredOrderId, setHoveredOrderId] = React.useState(null);

  return (
    <div className="table-wrap" style={{ overflowX: "auto" }}>
      <table ref={tableRef} className="notes-table" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th style={{ width: 90 }}>Order ID</th>
            <th>Customer</th>
            <th>Next Status</th>
            <th>Timeslot</th>
            <th>Type</th>
            <th>Payment</th>
            <th style={{ width: 120 }}>Order Total</th>
            <th>1st</th>
            <th>Area</th>
            <th style={{ width: 220 }}>Customer & Order Notes</th>
            <th style={{ width: 160 }}>Edited By</th>
            <th style={{ width: 100 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const bg = normalizeColor(r.color);
            const rowBackgroundColor = hoveredOrderId === r.order_id ? "#ffffff" : bg;

            return (
              <tr
                key={r.order_id}
                style={{ backgroundColor: rowBackgroundColor }}
                onMouseEnter={() => setHoveredOrderId(r.order_id)}
                onMouseLeave={() => setHoveredOrderId(null)}
              >
                <td>{r.order_id}</td>
                <td>{r.customer}</td>
                <td>{r.next_status_text}</td>
                <td>{r.next_ts || getServiceWindow(r) || "-"}</td>
                <td>{r.type}</td>
                <td>{r.payment ? paymentInfo(r.payment).label : "-"}</td>
                <td>{isNaN(Number(r.order_total)) ? r.order_total : Number(r.order_total || 0).toFixed(2)}</td>
                <td>{r.first_order ? "Yes" : "No"}</td>
                <td>{r.area || "-"}</td>
                <td
                  style={{ whiteSpace: "normal" }}
                  dangerouslySetInnerHTML={{ __html: r.notes_display || "-" }}
                />
                <td>{r.edited_by || "-"}</td>
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Link className="oc-btn ghost" to={`/orders/${r.order_id}`}>
                      view
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}

          {!rows.length && (
            <tr>
              <td colSpan={12} style={{ textAlign: "center", padding: 14 }}>
                No rows
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function labelForType(t) {
  if (t === "all") return "All";
  if (t === "car_cleaning") return "Car Cleaning";
  return t.charAt(0).toUpperCase() + t.slice(1);
}
